import type {
  DrawingEntity,
  QualityIssue,
  SourceRef,
  Point,
  EntityBase,
} from '../contract/types.js';
import type { Ledger } from './ledger.js';
import {
  splitSubPaths,
  subPathPoints,
  isClosed,
  bboxOf,
  curveHullPoints,
  type PathSegment,
} from './subpath.js';
import { bulgeOfSweep, recogniseArcSegment, recogniseCanonicalArc } from './arc.js';

export { splitSubPaths };
export type { PathSegment };

/**
 * Entity identifiers are produced by an explicit factory rather than a module
 * counter, so two documents can be extracted concurrently in the same process
 * without their identifiers colliding.
 */
export class IdFactory {
  private n = 0;
  constructor(private readonly prefix: string) {}
  next(): string {
    return `${this.prefix}-${++this.n}`;
  }
}

export interface StyleContext {
  layer: string;
  strokeWidth: number;
  colour: string;
  ids: IdFactory;
}

/** Below this, two points are the same point. */
const EPS = 1e-9;

export interface RecognitionResult {
  entities: DrawingEntity[];
  issues: QualityIssue[];
}

/**
 * Turns one sub-path into entities, or records why it could not become one.
 *
 * Every branch either consumes operators into an entity or records them as a
 * quality issue. There is no path through this function that drops an operator
 * without accounting for it.
 */
export function recogniseSubPath(
  sub: PathSegment[],
  style: StyleContext,
  source: SourceRef,
  ledger: Ledger,
): RecognitionResult {
  const entities: DrawingEntity[] = [];
  const issues: QualityIssue[] = [];
  const curves = sub.filter((s) => s.kind === 'curve');
  const allPoints = subPathPoints(sub);
  /**
   * The same subpath with its curves' turning points included.
   *
   * `allPoints` is the on-curve points, which is what the vertices of a polyline
   * are and is therefore right for geometry. It is wrong for a BOX: a cubic's
   * shape is in its control points, so a bounding box built from on-curve points
   * alone is the chord of every curve in the path. See `curveHullPoints`.
   */
  const boxPoints = curveHullPoints(sub);

  if (curves.length > 0) {
    const shape = recogniseCanonicalArc(sub);
    if (shape) {
      ledger.consume('curveTo', curves.length);
      const base = makeBase(style, source, bboxOf(boxPoints));
      entities.push(
        shape.type === 'CIRCLE'
          ? { ...base, type: 'CIRCLE', centre: shape.centre, radius: shape.radius }
          : {
              ...base,
              type: 'ARC',
              centre: shape.centre,
              radius: shape.radius,
              startAngle: shape.startAngle,
              endAngle: shape.endAngle,
            },
      );
      return { entities, issues };
    }

    // Not a circle or a single arc. Before refusing it, try the one other exact
    // reading: a path whose every curve segment is itself a circular arc is a
    // polyline with bulges, and a bulge *is* the arc rather than an
    // approximation of it. That is what answer 16 sanctions and it is how a
    // rounded rectangle — four edges and four quarter-circle corners — is
    // carried, since DXF has no primitive for one.
    const bulged = recogniseBulgedPolyline(sub);
    if (bulged) {
      const lineOps = Math.max(
        sub.filter((seg) => seg.kind === 'line' || seg.kind === 'close').length,
        1,
      );
      ledger.consume('curveTo', curves.length);
      ledger.consume('lineTo', lineOps);
      entities.push({
        ...makeBase(style, source, bboxOf(boxPoints)),
        type: 'LWPOLYLINE',
        vertices: bulged.vertices,
        closed: bulged.closed,
        bulges: bulged.bulges,
      });
      return { entities, issues };
    }

    /*
      Neither. The curve is recorded rather than flattened into short straight
      segments or fitted to an arc — both would be guesses that look like results.

      **But only the curve.** A subpath's straight segments are ordinary
      convertible geometry and used to be discarded along with it: this branch
      recorded `curveTo` and returned, never touching the line operators, so those
      segments produced no entity, no quality issue and no ledger entry, while the
      ledger went on reporting nothing unaccounted for.

      PDF producers emit exactly this shape constantly — `finish(closePath=True)`
      is PyMuPDF's own default, so a freeform outline arrives as `m … c … h` and
      the `h` is stroked ink. On fixture F01 that cost the straight edge
      (100, 794) → (280, 794): **180 pt, 63.5 mm**, present in the PDF and absent
      from both the trace and the DXF.
    */
    ledger.record('curveTo', curves.length);
    issues.push({
      kind: 'unsupported_curve',
      count: curves.length,
      pageIndex: source.pageIndex,
      bbox: bboxOf(boxPoints),
      operator: 'curveTo',
      detail: 'curve does not match the canonical arc construction; not approximated',
    });

    // The straight run through the same subpath, in the order the file draws it.
    const straight = straightRunOf(sub);
    if (straight.length >= 2) {
      const straightOps = Math.max(
        sub.filter((seg) => seg.kind === 'line' || seg.kind === 'close').length,
        1,
      );
      ledger.consume('lineTo', straightOps);
      const base = makeBase(style, source, bboxOf(straight));
      entities.push(
        straight.length === 2
          ? { ...base, type: 'LINE', start: straight[0]!, end: straight[1]! }
          : {
              ...base,
              type: 'LWPOLYLINE',
              vertices: straight,
              // Never closed: the run is a fragment of a shape whose curve was
              // refused, and calling it closed would assert a boundary the file
              // does not draw.
              closed: false,
              bulges: straight.map(() => 0),
            },
      );
    } else {
      // No straight segments to keep, but they still have to be accounted for if
      // the file drew any.
      const strayOps = sub.filter((seg) => seg.kind === 'line' || seg.kind === 'close').length;
      if (strayOps > 0) ledger.record('lineTo', strayOps);
    }
    return { entities, issues };
  }

  const pts = dedupeConsecutive(allPoints);
  const closed = isClosed(sub);
  const drawOps = Math.max(
    sub.filter((s) => s.kind === 'line' || s.kind === 'close').length,
    1,
  );

  if (pts.length < 2) {
    ledger.record('lineTo', drawOps);
    issues.push({
      kind: 'unsupported_operator',
      count: drawOps,
      pageIndex: source.pageIndex,
      bbox: bboxOf(allPoints),
      operator: 'lineTo',
      detail: 'degenerate sub-path with fewer than two distinct points',
    });
    return { entities, issues };
  }

  ledger.consume('lineTo', drawOps);
  const base = makeBase(style, source, bboxOf(pts));

  if (pts.length === 2 && !closed) {
    entities.push({ ...base, type: 'LINE', start: pts[0]!, end: pts[1]! });
  } else {
    entities.push({
      ...base,
      type: 'LWPOLYLINE',
      vertices: pts,
      closed,
      bulges: new Array<number>(pts.length).fill(0),
    });
  }

  return { entities, issues };
}

function makeBase(
  style: StyleContext,
  source: SourceRef,
  bbox: readonly [number, number, number, number],
): EntityBase {
  return {
    id: style.ids.next(),
    layer: style.layer,
    strokeWidth: style.strokeWidth,
    colour: style.colour,
    source: { ...source, bbox },
    origin: 'vector',
  };
}

/**
 * The on-curve points of a subpath's STRAIGHT segments, in draw order.
 *
 * A `line` contributes its endpoint and, being a segment, also the point it left —
 * which for a run following a curve is that curve's end. A `close` returns to the
 * subpath's first point. Curves contribute their endpoint only as the anchor a
 * following line starts from, never as geometry, because their shape is exactly
 * what could not be read.
 */
function straightRunOf(sub: PathSegment[]): Point[] {
  const out: Point[] = [];
  let cursor: Point | null = null;
  let first: Point | null = null;
  for (const seg of sub) {
    if (seg.kind === 'move') {
      cursor = seg.to;
      first = seg.to;
      continue;
    }
    if (seg.kind === 'curve') {
      cursor = seg.to;
      continue;
    }
    const to = seg.kind === 'close' ? first : seg.to;
    if (!to) continue;
    if (out.length === 0 && cursor) out.push(cursor);
    out.push(to);
    cursor = to;
  }
  return dedupeConsecutive(out);
}

function dedupeConsecutive(points: Point[]): Point[] {
  const out: Point[] = [];
  for (const q of points) {
    const last = out[out.length - 1];
    if (!last || Math.abs(last.x - q.x) > EPS || Math.abs(last.y - q.y) > EPS) out.push(q);
  }
  return out;
}

/**
 * A mixed path whose every curve is a circular arc, as a polyline with bulges.
 *
 * Returns null unless **every** curve segment is an arc. Partial success is not
 * an option here: a polyline with a bulge on the corners it recognised and a
 * straight chord across the one it did not is a shape the drawing does not
 * contain, and it would score as a near miss rather than as a refusal.
 *
 * A bulge belongs to the segment **leaving** its vertex, which is the whole of
 * the indexing: the arc from vertex `i` to vertex `i + 1` sets `bulges[i]`.
 */
interface BulgedPolyline {
  vertices: Point[];
  bulges: number[];
  closed: boolean;
}

function recogniseBulgedPolyline(sub: PathSegment[]): BulgedPolyline | null {
  const vertices: Point[] = [];
  const bulges: number[] = [];
  let closed = false;

  for (const seg of sub) {
    if (seg.kind === 'close') {
      closed = true;
      continue;
    }
    if (seg.kind === 'move') {
      // One sub-path, one start. A second move means the caller did not split.
      if (vertices.length > 0) return null;
      vertices.push(seg.to);
      bulges.push(0);
      continue;
    }
    if (vertices.length === 0) return null;

    if (seg.kind === 'line') {
      vertices.push(seg.to);
      bulges.push(0);
      continue;
    }

    const arc = recogniseArcSegment(vertices[vertices.length - 1]!, seg);
    if (!arc) return null;
    bulges[vertices.length - 1] = bulgeOfSweep(arc.sweep);
    vertices.push(seg.to);
    bulges.push(0);
  }

  if (vertices.length < 3) return null;

  // A path that returns to where it started carries that point once. The segment
  // that arrived there departed from the previous vertex and keeps its bulge, so
  // dropping the duplicate leaves the closing segment correctly described.
  const first = vertices[0]!;
  const last = vertices[vertices.length - 1]!;
  if (Math.abs(first.x - last.x) <= EPS && Math.abs(first.y - last.y) <= EPS) {
    vertices.pop();
    bulges.pop();
    closed = true;
  }

  if (vertices.length < 3) return null;
  if (!bulges.some((b) => b !== 0)) return null;
  return { vertices, bulges, closed };
}
